import store from "@/store/store";
import { Provider, useDispatch, useSelector } from "react-redux";
import React, { useEffect, useState } from "react";
import "rc-dropdown/assets/index.css";
import "rc-menu/assets/index.css";
import "./styles/globals.scss";
import "./styles/main.scss";
import MainLayout from "@/layouts/layout";
import "@xterm/xterm/css/xterm.css";
import axios from "axios";
import DatabaseSyncWrapper from "@/components/contexts/DatabaseSyncWrapper";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import { LiveblocksRoomWrapper } from "@/components/contexts/LiveblocksRoomWrapper";
import ErrorBoundary from "@/components/ErrorBoundary";
import MaintenanceModal from "@/components/MaintenanceModal";
import * as Sentry from "@sentry/react";
import Home from "./pages/index";
import ExtensionLogin from "./pages/extensionLogin";
import LocalLogin from "./pages/localLogin";
import AINew from "./pages/project-create/ai-new";
import ExistingProject from "./pages/project-create/existing-project";
import WorkflowSelect from "./pages/project-create/workflow-select";
import Editor from "./pages/editor/index";
import AccessRequests from "./pages/access-requests";
import FigmaCallback from "./pages/figma/callback";
import GithubSuccess from "./pages/GithubSuccess";
import { Navigate, Route, Routes, useRoutes } from "react-router";
import routes from "tempo-routes";
import usePlanInfoStore from "./store/zustand/planInfoStore";
import Loading from "./components/ui/loading";
import Intercom from "@intercom/messenger-js-sdk";
import { datadogRum } from "@datadog/browser-rum";

import {
  useUser,
  useSession,
  SignedIn,
  useClerk,
  useAuth,
  ClerkProvider,
  SignIn,
  SignUp,
} from "@clerk/clerk-react";
import { dark } from "@clerk/themes";

import { useIsEmailOnly } from "./utils/utils";
import { useUserId } from "./utils/utils";
import { getUnexpiredToken } from "./utils/utils";

if (typeof window !== "undefined") {
  // checks that we are client-side
  posthog.init(import.meta.env.VITE_POSTHOG_KEY, {
    api_host: import.meta.env.VITE_POSTHOG_HOST || "https://us.i.posthog.com",
    loaded: (posthog) => {
      posthog.debug(import.meta.env.VITE_LOG_POSTHOG === "true");
    },
    session_recording: {
      recordCrossOriginIframes: true,

      maskAllInputs: false,
      maskInputOptions: {
        password: true,
      },
    },
  });

  if (window.location.hostname !== "localhost") {
    Sentry.init({
      dsn: "https://c602bcd4c514223aceb028bdc2d8ac87@o4508337261707264.ingest.us.sentry.io/4508337262690304",
      integrations: [Sentry.browserTracingIntegration()],
      // Tracing
      tracesSampleRate: 1.0, //  Capture 100% of the transactions
      // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
      tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
    });

    datadogRum.init({
      applicationId: "0ee0e448-34aa-4f2b-9b12-2f5c7b6a9b5d",
      clientToken: "pubd1a0840858821718704e2220295a8c92",
      // `site` refers to the Datadog site parameter of your organization
      // see https://docs.datadoghq.com/getting_started/site/
      site: "us5.datadoghq.com",
      service: "tempo-web",
      env: import.meta.env.VITE_ENVIRONMENT?.toLowerCase(),
      // Specify a version number to identify the deployed version of your application in Datadog
      // version: '1.0.0',
      sessionSampleRate: 100,
      sessionReplaySampleRate: 20,
      defaultPrivacyLevel: "allow",
    });
  }

  // jam.metadata(() => {
  //   const data = sessionStorage.getItem(JAM_METADATA) || "{}";
  //   try {
  //     return JSON.stringify(data);
  //   } catch (e) {
  //     return {};
  //   }
  // });
}

function IdentityWrapper({ children }: any) {
  const { user } = useUser();
  const { isLoaded, session } = useSession();
  const fetchReferral = usePlanInfoStore((state) => state.fetchReferral);
  const refreshPlanInfo = usePlanInfoStore((state) => state.refreshPlanInfo);

  const [initialized, setInitialized] = useState(false);
  const isEmailOnly = useIsEmailOnly();
  const userId = useUserId();

  useEffect(() => {
    fetchReferral();
  }, []);

  useEffect(() => {
    const manuallySetExternalId = async () => {
      // Check number of attempts
      const urlParams = new URLSearchParams(window.location.search);
      const attempts = parseInt(urlParams.get("external-id-att") || "0");

      if (attempts >= 5) {
        console.warn(
          "Maximum number of manual update attempts reached (5), stopping to prevent infinite reload"
        );
        return;
      }

      await axios.post(
        `${
          import.meta.env.VITE_BACKEND_BASE_URL
        }/clerk/webhook/manual-update-user`,
        {},
        {
          headers: {
            Authorization: `Bearer ${await getUnexpiredToken(session)}`,
          },
        }
      );

      // Increment attempt counter and reload
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set("external-id-att", (attempts + 1).toString());
      window.location.href = newUrl.toString();
    };

    const identifyUser = async () => {
      if (initialized) {
        return;
      }

      if (!isLoaded || !user || !session) {
        return;
      }

      if (!user?.externalId) {
        manuallySetExternalId();
        return;
      }

      setInitialized(true);

      const githubUser = user?.externalAccounts.find(
        (account) => account.provider === "github"
      );

      let githubUrl = "";
      if (githubUser) {
        try {
          const githubUserId = `github|${githubUser?.providerUserId}`;
          const res = await axios.get(
            `https://api.github.com/user/${githubUserId.split("|")[1]}`
          );
          githubUrl = res.data.html_url;
        } catch (e) {
          console.error("Error fetching github user", e);
        }
      }

      // If no external ID, for debugging only, use the clerk ID specifically
      posthog.identify(userId || user?.id, {
        email: user.primaryEmailAddress?.emailAddress,
        name: user.fullName,
        environment: import.meta.env.VITE_ENVIRONMENT,
        github_url: githubUrl,
      });

      Intercom({
        app_id: "ln7m3c5n",
        user_id: userId || user?.id,
        name: user.fullName || "",
        email: user.primaryEmailAddress?.emailAddress,
        hide_default_launcher: true,
      });
    };

    identifyUser();
  }, [user, isLoaded, Boolean(session)]);

  useEffect(() => {
    if (!session) return;

    refreshPlanInfo(session);
  }, [Boolean(session)]);

  if (!isLoaded || !session || !user) {
    return (
      <div className="dark w-full h-full">
        <Loading size={32} />
      </div>
    );
  }

  return <>{children}</>;
}

const RouteWrapper = ({ element: Element, ...rest }) => {
  const { redirectToSignIn } = useClerk();
  const { userId, isLoaded } = useAuth();

  if (!userId && isLoaded) {
    redirectToSignIn();
    return null;
  }

  return (
    <SignedIn>
      <Provider store={store}>
        <PostHogProvider client={posthog}>
          <IdentityWrapper>
            <LiveblocksRoomWrapper>
              <DatabaseSyncWrapper>
                <MainLayout>
                  <Element {...rest} />
                </MainLayout>
              </DatabaseSyncWrapper>
            </LiveblocksRoomWrapper>
          </IdentityWrapper>
        </PostHogProvider>
      </Provider>
    </SignedIn>
  );
};

const ClerkWrapper = ({ children }: { children: React.ReactNode }) => {
  const clerkAppearance = {
    baseTheme: dark,
    layout: {
      socialButtonsPlacement: "top" as const,
      socialButtonsVariant: "blockButton" as const,
    },
    elements: {
      socialButtons: "flex flex-col gap-2 w-full",
      socialButtonsBlockButton:
        "w-full h-10 flex items-center justify-center gap-2 text-sm font-medium",
      formButtonPrimary: "w-full",
      card: "shadow-lg",
    },
  };

  // Determine primary and satellite domains based on Clerk domain
  const clerkDomain = import.meta.env.VITE_CLERK_DOMAIN || "tempo.new";
  const primaryDomain = clerkDomain;
  const satelliteDomains = [
    "tempo-dev.app",
    clerkDomain === "tempo.build" ? "tempo.new" : "tempo.build",
  ];

  // Check if current domain is a satellite domain
  if (
    satelliteDomains.some((domain) => window.location.host?.endsWith(domain)) &&
    import.meta.env.VITE_ENVIRONMENT === "PROD"
  ) {
    // Satellite domain - use satellite provider
    return (
      <ClerkProvider
        publishableKey={import.meta.env.VITE_CLERK_PUBLISHABLE_KEY}
        isSatellite
        signInUrl={
          import.meta.env.VITE_CLERK_SIGN_IN_URL ||
          `https://app.${primaryDomain}/sign-in`
        }
        signUpUrl={
          import.meta.env.VITE_CLERK_SIGN_UP_URL ||
          `https://app.${primaryDomain}/sign-up`
        }
        domain={(url) => url.host}
        appearance={clerkAppearance}
      >
        {children}
      </ClerkProvider>
    );
  } else {
    // If the origin is the primary
    return (
      <ClerkProvider
        publishableKey={import.meta.env.VITE_CLERK_PUBLISHABLE_KEY}
        allowedRedirectOrigins={[
          "https://app.tempo-dev.app",
          "https://tempo-dev.app",
          "https://app.tempo.new",
          "https://tempo.new",
          "https://app.tempo.build",
          "https://tempo.build",
        ]}
        signInUrl={`${window.location.origin}/sign-in`}
        signUpUrl={`${window.location.origin}/sign-up`}
        appearance={clerkAppearance}
      >
        {children}
      </ClerkProvider>
    );
  }
};

function Root() {
  const ErrorBoundaryComp = import.meta.env.DEV
    ? React.Fragment
    : ErrorBoundary;

  return (
    <ErrorBoundaryComp>
      <ClerkWrapper>
        {/* Maintenance Modal - displays on top of everything when maintenance mode is active */}
        <MaintenanceModal />

        {/* Tempo routes */}
        {import.meta.env.VITE_TEMPO && useRoutes(routes)}

        <Routes>
          <Route index element={<RouteWrapper element={Home} />} />
          <Route
            path="extensionLogin"
            element={<RouteWrapper element={ExtensionLogin} />}
          />
          <Route
            path="sign-in"
            element={
              <div className="dark w-screen h-screen flex justify-center items-center">
                <SignIn
                  signUpUrl="/sign-up"
                  appearance={{
                    elements: {
                      socialButtonsBlockButton:
                        "w-full h-10 flex items-center justify-center gap-2 text-lg font-medium",
                    },
                  }}
                />
              </div>
            }
          />
          <Route
            path="sign-up"
            element={
              <div className="dark w-screen h-screen flex justify-center items-center">
                <SignUp
                  signInUrl="/sign-in"
                  appearance={{
                    elements: {
                      socialButtonsBlockButton:
                        "w-full h-10 flex items-center justify-center gap-2 text-lg font-medium",
                    },
                  }}
                />
              </div>
            }
          />
          <Route
            path="localLogin"
            element={<RouteWrapper element={LocalLogin} />}
          />

          <Route path="project-create">
            <Route path="ai-new" element={<RouteWrapper element={AINew} />} />
            <Route
              path="existing-project"
              element={<RouteWrapper element={ExistingProject} />}
            />
            <Route
              path="workflow-select"
              element={<RouteWrapper element={WorkflowSelect} />}
            />
          </Route>

          <Route path="canvases/:canvasId">
            <Route index element={<Navigate to="editor" replace />} />
            <Route path="editor" element={<RouteWrapper element={Editor} />} />
            <Route
              path="webc-editor"
              element={<RouteWrapper element={Editor} />}
            />
          </Route>

          <Route
            path="access-requests/:token"
            element={<RouteWrapper element={AccessRequests} />}
          />

          <Route
            path="figma/callback"
            element={<RouteWrapper element={FigmaCallback} />}
          />
          <Route
            path="github/success"
            element={<RouteWrapper element={GithubSuccess} />}
          />
        </Routes>
      </ClerkWrapper>
    </ErrorBoundaryComp>
  );
}

export default Root;
